import React, { useState, useEffect, useRef } from 'react';
import { Star, ShieldCheck, CheckCircle2, ArrowRight, ExternalLink, MapPin, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { REVIEWS, Review, SITE_CONTACT } from '../data/siteData';

interface TestimonialsSectionProps {
  onOpenBooking: () => void;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ onOpenBooking }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll loop with continuous infinite wrapping
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % REVIEWS.length);
      }, 4200);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % REVIEWS.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + REVIEWS.length) % REVIEWS.length);
  };

  // 3 reviews in view on desktop, looped infinitely
  const getVisibleReviews = () => {
    const total = REVIEWS.length;
    return [
      REVIEWS[currentIndex % total],
      REVIEWS[(currentIndex + 1) % total],
      REVIEWS[(currentIndex + 2) % total],
    ];
  };

  return (
    <section id="reviews" className="py-20 lg:py-28 bg-white border-b border-slate-200 overflow-hidden text-left">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Section Header with Official Google Trust Badge */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <div className="text-xs font-mono font-bold uppercase tracking-widest text-[#b88e4c] mb-2">
              Verified Social Proof · Google Business Profile API
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-serif-heading">
              Trusted by operators <br className="hidden sm:block" />
              and founders.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
              Direct verification from founders and managing directors across East Africa, auto-synced live from our Google Business Profile.
            </p>
          </div>

          {/* Official Google Rating Pill Lockup (Auto-Synced, Manual Sync Button Hidden) */}
          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 shadow-xs self-start lg:self-auto">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-extrabold text-2xl shadow-xs shrink-0 font-serif-heading">
                <span className="text-[#009fe3]">G</span>
              </div>
              <div>
                <div className="flex items-center gap-1 text-[#b88e4c] mb-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#b88e4c] text-[#b88e4c]" />
                  ))}
                  <span className="ml-1 text-xs font-bold text-slate-900 font-mono">5.0</span>
                </div>
                <div className="text-xs font-bold text-slate-900 font-sans">
                  11 Verified Google Reviews
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  Place ID: {SITE_CONTACT.googlePlaceId}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200 w-full sm:w-auto justify-end">
              <div className="flex items-center gap-1.5 text-xs font-mono text-slate-500">
                <span className="h-2 w-2 rounded-full bg-[#009fe3] animate-pulse" />
                <span>Auto-Synced</span>
              </div>

              <a
                href={SITE_CONTACT.googleReviewUrl}
                target="_blank"
                rel="noreferrer"
                className="px-3.5 py-2 rounded-lg bg-slate-900 hover:bg-[#b88e4c] text-white font-bold text-xs flex items-center gap-1.5 transition-colors whitespace-nowrap shadow-xs font-sans"
              >
                <span>Write Review</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Carousel Controls Bar */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
            <span>Verified Place ID: {SITE_CONTACT.googlePlaceId}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Play / Pause Toggle */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-800 hover:bg-slate-50 text-xs flex items-center gap-1.5 cursor-pointer min-h-[44px]"
              title={isPlaying ? 'Pause Auto-Play' : 'Resume Auto-Play'}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 text-[#b88e4c]" />}
              <span className="text-xs font-semibold hidden sm:inline">{isPlaying ? 'Pause' : 'Play'}</span>
            </button>

            {/* Prev / Next Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                className="p-3 rounded-lg border border-slate-300 hover:bg-slate-100 text-slate-800 transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Previous review"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={handleNext}
                className="p-3 rounded-lg border border-slate-300 hover:bg-slate-100 text-slate-800 transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Next review"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Infinity Carousel Container */}
        <div
          onMouseEnter={() => setIsPlaying(false)}
          onMouseLeave={() => setIsPlaying(true)}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-300"
        >
          {getVisibleReviews().map((review, idx) => (
            <div
              key={`${review.id}-${currentIndex}-${idx}`}
              className="rounded-3xl bg-slate-50 border border-slate-200 p-6 sm:p-7 flex flex-col justify-between text-left hover:border-[#b88e4c]/50 hover:bg-white hover:shadow-lg transition-all duration-300 animate-in fade-in"
            >
              <div>
                {/* Author row & rating */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm font-serif-heading">
                      {review.author[0]}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-slate-900 font-sans">
                        {review.author}
                      </div>
                      <div className="text-xs text-slate-400 font-mono">
                        {review.date}
                      </div>
                    </div>
                  </div>

                  <div className="flex text-[#b88e4c]">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-[#b88e4c] text-[#b88e4c]" />
                    ))}
                  </div>
                </div>

                <div className="text-xs text-[#0077b5] bg-[#009fe3]/10 px-2 py-0.5 rounded inline-block font-mono font-bold mb-3 border border-[#009fe3]/30">
                  Verified Google Review · 5.0
                </div>

                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed font-normal">
                  "{review.content}"
                </p>
              </div>

              {/* Owner's Reply */}
              {review.ownerReply && (
                <div className="mt-4 pt-3 border-t border-slate-200 bg-white p-3 rounded-xl text-left border border-slate-100">
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5 mb-1 font-sans">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#009fe3]" />
                    <span>Response from Thinkers Arcade:</span>
                  </div>
                  <p className="text-xs text-slate-600 italic">
                    "{review.ownerReply}"
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Carousel Pagination Dots with 44px+ Accessible Touch Targets */}
        <div className="flex items-center justify-center gap-1 mt-8">
          {REVIEWS.map((_, dotIdx) => (
            <button
              key={dotIdx}
              onClick={() => setCurrentIndex(dotIdx)}
              className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer rounded-full transition-colors"
              aria-label={`Jump to review ${dotIdx + 1}`}
            >
              <span
                className={`h-2.5 rounded-full transition-all block ${
                  currentIndex % REVIEWS.length === dotIdx
                    ? 'w-7 bg-[#b88e4c]'
                    : 'w-2.5 bg-slate-400 hover:bg-slate-600'
                }`}
              />
            </button>
          ))}
        </div>

      </div>
    </section>
  );
};
